import { isMultimodalContent } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/doc_summarization";
import { getConnectorDisplayNameFromResult } from "@mirage/shared/connectors";
import { getPersonFilter } from "@mirage/shared/search/search-filters";
import { titleTokenizer } from "@mirage/shared/search/tokenizers";
import {
  getEventTimeString,
  getMinutesAndHoursUntilOrFromStartOfEvent,
} from "@mirage/shared/util/calendar";
import { highlightTokenizer } from "@mirage/shared/util/stop-words";
import { getAbbrDate, getTimeAgoString } from "@mirage/shared/util/time";
import i18n from "@mirage/translations";
import {
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  formatDuration,
  intervalToDuration,
  isToday,
  isTomorrow,
} from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

import type { dash } from "@dropbox/api-v2-client";
import type { SearchResult } from "@mirage/service-dbx-api";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import type { AuthorInfo, Modifier } from "@mirage/shared/search/search-result";
import type { I18nCreateByKey } from "@mirage/shared/translations/I18nCreateByKey";
import type { I18nKey } from "@mirage/translations";
import type { Chunk, FindChunks } from "react-highlight-words";

function getTimeToEvent(startTime: number, endTime: number): string {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);
  const currentDate = new Date();

  const sDelta = differenceInSeconds(startDate, currentDate);
  const mDelta = differenceInMinutes(startDate, currentDate);
  const hDelta = differenceInHours(startDate, currentDate);

  if (startDate < currentDate) {
    // Event has already started
    if (sDelta > -60) {
      return "Just now";
    } else if (currentDate < endDate) {
      return "Now";
    } else {
      return getTimeAgoString(endTime, {
        capitalize: true,
      });
    }
  } else {
    // Event is in the future
    if (sDelta < 60) {
      return "Less than 1 min";
    } else if (mDelta < 60) {
      return mDelta === 1 ? "In 1 minute" : `In ${mDelta} minutes`;
    } else if (isToday(startDate) && hDelta >= 1) {
      return hDelta === 1 ? `In 1 hour` : `In ${hDelta} hours`;
    } else if (isTomorrow(startDate)) {
      return "Tomorrow";
    } else {
      return format(startDate, "MMM d, yyyy");
    }
  }
}

function epochMsToDisplayTime(epoch: number): string {
  return new Date(epoch)
    .toLocaleString("en-US", { timeStyle: "short" })
    .toLowerCase()
    .replace(/\s/g, "");
}

export function getEventTimeToDisplay(result: SearchResult): string | null {
  const { isAllDay, startTime, endTime } = result;

  let timeUntilOrFrom = null;
  if (startTime && startTime > 0 && endTime && endTime > 0) {
    const now = Date.now();
    if (!isAllDay && startTime < now && now < endTime) {
      const { hours, minutes } = getMinutesAndHoursUntilOrFromStartOfEvent(
        result,
        "in_progress",
      );
      timeUntilOrFrom =
        getEventTimeString("in_progress", hours, minutes) || null;
    } else {
      timeUntilOrFrom = getTimeToEvent(
        // TODO: Temporary fix until https://jira.dropboxer.net/browse/OTCIP-46 is
        // resolved and the API provides just the date for these all-day events.
        isAllDay ? utcToZonedTime(startTime, "UTC").getTime() : startTime,
        isAllDay ? utcToZonedTime(endTime, "UTC").getTime() : endTime,
      );
    }
  }
  return timeUntilOrFrom;
}

export function getDuration(start: number, end: number): string {
  const duration = intervalToDuration({ start, end });
  // hh:mm:ss for events longer than 1 hour and mm:ss for shorter
  const hDelta = differenceInHours(end, start);
  const displayOptions = {
    format:
      hDelta > 0 ? ["hours", "minutes", "seconds"] : ["minutes", "seconds"],
    zero: true,
    delimiter: ":",
    locale: {
      formatDistance: (_: unknown, timeUnits: number) =>
        String(timeUnits).padStart(2, "0"),
    },
  };
  const durationForDisplay = formatDuration(duration, displayOptions);

  return durationForDisplay;
}

export function getCalendarSubtitle(result: SearchResult) {
  const { isAllDay, startTime, endTime } = result;

  let timeRange = null;
  if (isAllDay) {
    timeRange = "All day";
  } else if (startTime && startTime > 0 && endTime && endTime > 0) {
    timeRange = `${epochMsToDisplayTime(startTime)} - ${epochMsToDisplayTime(
      endTime,
    )}`;
  }

  const timeUntilOrFrom = getEventTimeToDisplay(result);

  if (timeRange && timeUntilOrFrom) {
    return `${timeRange} · ${timeUntilOrFrom}`;
  } else if (!timeRange && timeUntilOrFrom) {
    return timeUntilOrFrom;
  } else if (timeRange && !timeUntilOrFrom) {
    return timeRange;
  }
  return "";
}

export function getLocalFileTimestamp(result: SearchResult | Recommendation) {
  if (result.filteredModifier?.modifiedAtMs) {
    const timeAgo = getTimeAgoString(result.filteredModifier?.modifiedAtMs);
    return i18n.t("updated_ago", { timeAgo });
  }

  if (result.providerUpdateAtMs) {
    const timeAgo = getTimeAgoString(result.providerUpdateAtMs);
    return i18n.t("updated_ago", { timeAgo });
  }

  return null;
}

export function isMeetingJoinable(result: SearchResult) {
  if (
    result?.recordType?.[".tag"] === "event" &&
    result?.conferenceLinks?.length
  ) {
    return true;
  }
  return false;
}

export const getTimeAgoStringFromTimestamp = (
  pastDate?: number | null,
  noPretext?: boolean,
) => {
  let timeAgo = "";

  if (pastDate) {
    const timeAgoString = getTimeAgoString(pastDate, {
      abbreviateTime: false,
      abbreviateDate: true,
    });
    timeAgo = noPretext
      ? timeAgoString
      : i18n.t("updated_ago", { timeAgo: timeAgoString });
  }

  return timeAgo;
};

type NullableAuthorInfo = AuthorInfo | null | undefined;
type NullableModifier = Modifier | null | undefined;
export type PersonForMetadata = {
  i18nKey: I18nCreateByKey | undefined;
  person: NullableAuthorInfo;
};
export const getPersonForMetadata = ({
  sender,
  creator,
  lastModifier,
  assignedTo,
  activeFilters,
  filteredModifier,
}: {
  sender: NullableAuthorInfo;
  creator: NullableAuthorInfo;
  lastModifier: NullableAuthorInfo;
  assignedTo?: NullableAuthorInfo;
  activeFilters?: SearchFilter[];
  filteredModifier?: NullableModifier;
}): {
  i18nKey: I18nCreateByKey | undefined;
  person: NullableAuthorInfo;
} => {
  const activePersonFilter = getPersonFilter(activeFilters || []);
  const emailToMatch = activePersonFilter?.parameters?.email;

  const persons: {
    person: NullableAuthorInfo;
    i18nKey: I18nCreateByKey;
  }[] = [
    // Note: This is ordered by priority of which author type we want to display
    { person: assignedTo, i18nKey: "result_assigned_to" },
    { person: sender, i18nKey: "result_sent_by" },
    {
      person: filteredModifier?.author ?? lastModifier,
      i18nKey: "result_updated_by",
    },
    { person: creator, i18nKey: "result_created_by" },
  ];

  // If a person filter is active, try to match it first
  if (emailToMatch) {
    for (const { person, i18nKey } of persons) {
      if (person?.email === emailToMatch) {
        return { person, i18nKey };
      }
    }
  }

  // Fallback to the first available person with displayable attributes
  for (const { person, i18nKey } of persons) {
    if (person?.displayName || person?.email) {
      return { person, i18nKey };
    }
  }

  // Default return if no matches or data available
  return { person: undefined, i18nKey: undefined };
};

export const isSummarizable = (
  result: SearchResult,
  enableMultimodal: boolean,
  enableSpreadsheets: boolean,
  multimodalFileTypeFilter: { [key: string]: string[] },
) => {
  const isMultimodalSummarizationEnabled =
    enableMultimodal &&
    isMultimodalContent(
      result.connectorInfo.connectorName,
      result.fileTypeInfo?.id || "",
      multimodalFileTypeFilter,
    );
  const isSpreadsheetSummarizationEnabled =
    enableSpreadsheets &&
    (result.fileTypeInfo?.id === "google_sheet" ||
      result.fileTypeInfo?.id === "microsoft_excel");
  return (
    result.summarizable == "yes_summarizable" ||
    isMultimodalSummarizationEnabled ||
    isSpreadsheetSummarizationEnabled
  );
};

type FindChunksFn = (options: FindChunks) => Chunk[];

export function findHighlightableChunksInQuery(
  query: string,
): [FindChunksFn, string[]] {
  const searchWords: string[] = highlightTokenizer(query); // Handle STOP words, preprocess tokens

  const findChunks: FindChunksFn = function (options: FindChunks): Chunk[] {
    function escapeRegExpFn(str: string): string {
      return str.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
    }

    return searchWords.reduce(function (chunks, searchWord) {
      if (typeof options.sanitize === "function") {
        searchWord = options.sanitize(searchWord);
      }

      if (options.autoEscape) {
        searchWord = escapeRegExpFn(searchWord);
      }

      const wordsToHighlight = new Set(titleTokenizer(options.textToHighlight));

      const regex = new RegExp(searchWord, options.caseSensitive ? "g" : "gi");

      let match = void 0 as unknown as RegExpExecArray | null;
      while ((match = regex.exec(options.textToHighlight))) {
        const start = match.index;
        const end = regex.lastIndex;
        if (
          end > start &&
          wordsToHighlight.has(options.textToHighlight.slice(start, end))
        ) {
          chunks.push({ start: start, end: end });
        }

        // Prevent browsers like Firefox from getting stuck in an infinite loop
        // See http://www.regexguru.com/2008/04/watch-out-for-zero-length-matches/
        if (match.index == regex.lastIndex) {
          regex.lastIndex++;
        }
      }

      return chunks;
    }, [] as Chunk[]);
  };

  return [findChunks, searchWords];
}

// only these file types for now
// see https://jira.dropboxer.net/browse/OSE-4430
const fileTypesWithSubApp = ["google_doc", "google_sheet", "google_slide"];
type FileTypeWithSubApp = (typeof fileTypesWithSubApp)[number];

function isFileTypeWithSubApp(
  fileTypeId: string,
): fileTypeId is FileTypeWithSubApp {
  return fileTypesWithSubApp.includes(fileTypeId);
}

const fileTypeIdToAppNameKey: Record<FileTypeWithSubApp, I18nKey> = {
  google_doc: "google_docs",
  google_sheet: "google_sheets",
  google_slide: "google_slides",
};

export function getAppDisplayNameFromResult(
  result: SearchResult | Recommendation,
) {
  const fileTypeId = result.fileTypeInfo?.id ?? "";
  if (isFileTypeWithSubApp(fileTypeId)) {
    return i18n.t(fileTypeIdToAppNameKey[fileTypeId]);
  }
  return getConnectorDisplayNameFromResult(result);
}

export function getVirtualPathSegments(
  result: SearchResult | Recommendation,
): string[] {
  if (result.virtualPath && result.virtualPath.length > 1) {
    return result.virtualPath.slice(0, -1); // last item in path is title
  }

  return [];
}

export function getPathForDisplay(
  pathSegments: string[],
  isTooltipContent?: boolean,
) {
  if (isTooltipContent) {
    return pathSegments.join(" > ");
  }
  return pathSegments.at(-1) || "";
}

export function shouldDisplayVirtualPath(pathSegments: string[]): boolean {
  return pathSegments.length > 0 && getPathForDisplay(pathSegments) !== "";
}

export function getLocalFilePathSegments(result: SearchResult): string[] {
  return result?.desktopLocalFileMetadata?.pathSegments || [];
}

export function getAttachments(result: SearchResult): dash.LinkAttachment[] {
  // OSE-5196: Once messages have attachments, ensure this field is correct
  return result.attachments;
}

export function getTimeAndDate(messageTs: number): string {
  const sentToday = isToday(messageTs);
  const time = epochMsToDisplayTime(messageTs);
  if (sentToday) {
    return i18n.t("today_at_time", { time });
  }
  const date = getAbbrDate(messageTs);
  return i18n.t("date_at_time", { date, time });
}

export function usesPersonIconAsMainIcon(result: SearchResult): boolean {
  const sender = result?.sender ?? result?.creator;
  return (
    (result.fileTypeInfo?.id === "email" ||
      result.fileTypeInfo?.id === "chat") &&
    !!sender &&
    (!!sender.displayName || !!sender.email)
  );
}
